// === SHARED: audioBus ===
// A tiny, dependency-free singleton that coordinates the page's interchangeable
// audio sources so that only ONE ever plays at a time. Every audio source
// (StaticFM, MusicBumper, CassetteDeck, ...) registers itself by a stable string
// id along with a pauseFn that pauses ITSELF. When a source starts playing it
// calls claim(id, pauseFn); the bus records it as the current owner and, if some
// OTHER source previously owned the bus, calls that source's pauseFn() to pause
// it instantly. When a source stops, it calls release(id) to give up ownership.
//
// LIVE LEVEL: the current owner may ALSO expose a "level" function — a 0..1
// reading of how loud it is RIGHT NOW (e.g. the average of an AnalyserNode's
// bands). Any on-screen visual (the faceless dancers) can then call
// audioBus.getLevel() once per frame and react to WHATEVER source is currently
// playing, without knowing or caring which one it is. When nothing is playing
// (no owner, or the owner has no level function, or it throws) getLevel()
// returns 0, so the visual simply rests.
//
// Design notes:
//  - No React, no DOM, no timers. Just a single module-level record.
//  - Null-safe everywhere: missing ids / pauseFns / levelFns are ignored, never
//    thrown; getLevel() always returns a finite number in [0, 1].
//  - claim() with the SAME id that already owns the bus is a no-op re-claim
//    (it just refreshes the stored pauseFn + levelFn) and never pauses itself.
//  - There is exactly one owner at a time (or none).

// The single owner record. `null` means nothing is currently claiming audio.
// Shape: { id: string, pause: function|null, level: function|null }
let owner = null;

// Clamp anything (NaN/undefined/out-of-range) into a finite 0..1 level.
function clamp01(n) {
  if (typeof n !== "number" || !isFinite(n)) return 0;
  if (n <= 0) return 0;
  if (n >= 1) return 1;
  return n;
}

/**
 * Record `id` as the current audio owner. If a DIFFERENT source currently owns
 * the bus, its previously-registered pauseFn is invoked to pause it.
 *
 * @param {string} id        Stable identifier for the claiming source.
 * @param {Function} [pauseFn]  Called (with no args) to pause THIS source when
 *                              some other source later claims the bus.
 * @param {Function} [levelFn]  Optional. Called (with no args) and must return
 *                              this source's CURRENT loudness as a number in
 *                              [0, 1] (0 = silent). Read by getLevel() while
 *                              this source owns the bus. May be omitted; it can
 *                              also be (re)attached later via setLevelProvider.
 */
function claim(id, pauseFn, levelFn) {
  // Ignore meaningless claims rather than throwing — keeps callers simple.
  if (id == null) return;

  const pause = typeof pauseFn === "function" ? pauseFn : null;
  const level = typeof levelFn === "function" ? levelFn : null;

  // Re-claim by the same owner: just refresh the pauseFn (and the levelFn IF a
  // new one was supplied — a re-claim that omits levelFn keeps the existing one
  // so callers passing only (id, pauseFn) don't wipe a previously-set provider).
  if (owner && owner.id === id) {
    owner.pause = pause;
    if (level) owner.level = level;
    return;
  }

  // A different source owned the bus — pause it before taking over.
  if (owner && typeof owner.pause === "function") {
    try {
      owner.pause();
    } catch (e) {
      // A misbehaving pauseFn must never break the source that's starting up.
      // Swallow and continue handing ownership over.
    }
  }

  owner = { id, pause, level };
}

/**
 * Attach (or replace) the live-level function for `id`, but ONLY if `id` is the
 * current owner. A no-op otherwise, so a source that has already been pre-empted
 * can call this safely. Pass a non-function to clear the provider.
 *
 * @param {string} id       The id setting its level provider.
 * @param {Function} levelFn  Returns this source's current loudness in [0, 1].
 */
function setLevelProvider(id, levelFn) {
  if (id == null) return;
  if (owner && owner.id === id) {
    owner.level = typeof levelFn === "function" ? levelFn : null;
  }
}

/**
 * The CURRENT owner's live loudness as a number in [0, 1].
 *
 * @returns {number} 0..1 — the owner's level via its levelFn, or 0 when nothing
 *   is playing / the owner exposed no level / the levelFn throws or returns a
 *   non-finite value. Always a finite number; never throws.
 */
function getLevel() {
  if (!owner || typeof owner.level !== "function") return 0;
  let v;
  try {
    v = owner.level();
  } catch (e) {
    // A misbehaving level provider must never break the visual reading it.
    return 0;
  }
  return clamp01(v);
}

/**
 * Relinquish ownership if (and only if) `id` currently owns the bus. Calling
 * release for an id that does not own the bus is a harmless no-op, so a source
 * that was already pre-empted by another can still safely call release on stop.
 *
 * @param {string} id  The id that is releasing.
 */
function release(id) {
  if (id == null) return;
  if (owner && owner.id === id) {
    owner = null;
  }
}

/**
 * @param {string} id
 * @returns {boolean} true if `id` is the current owner of the bus.
 */
function isCurrent(id) {
  return !!owner && owner.id === id;
}

const audioBus = { claim, release, isCurrent, setLevelProvider, getLevel };

export default audioBus;
